import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/hooks/useToast.ts'
import i18n from '@/i18n.ts'
import { LOCAL_STORAGE_LANG_KEY } from '@/lib/constants/constants.ts'
import { shouldQueueMutation } from '@/lib/platform/platformDetection'
import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'
import { Network } from '@capacitor/network'
import { getApiBaseUrl } from '@/config/apiConfig'
import { slowConnectionDetector } from '@/lib/slowConnectionDetector'

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[API]', ...args)

/**
 * Map API URL segments to React Query cache keys that should be invalidated
 * when a mutation (POST/PATCH/DELETE) succeeds on that endpoint.
 * Broad invalidation is intentional — ensures all related views stay fresh.
 * Add new entries here when adding new API resources or query keys.
 */
const MUTATION_INVALIDATION_MAP: Record<string, string[]> = {
  '/issues': ['singleIssue', 'stageIssuesByIssueType', 'singleReportIssues', 'getIssuesSummary', 'getIssueTypeArchiveQuery'],
  '/comments': ['singleIssueComments', 'singleIssueEvents'],
  '/media_objects': ['singleIssue', 'singleReportIssues'],
  '/reports': ['singleReport', 'singleReportIssues', 'getReportsQuery'],
  '/notifications': ['getNotificationsSummary', 'getNotificationsByStageId', 'getUnreadNotificationsByStageId'],
  '/locations': ['stageLocations', 'getProjectStageLocations'],
  '/statuses': ['projectStatusGroups', 'organizationStatusGroups'],
  '/status_groups': ['projectStatusGroups', 'organizationStatusGroups'],
  '/labels': ['projectLabelGroups', 'organizationLabelGroups'],
  '/label_groups': ['projectLabelGroups', 'organizationLabelGroups'],
  '/issue_types': ['projectIssueTypes', 'organizationIssueTypes', 'currStageIssueTypes'],
  '/project_stages': ['orgProjectStages', 'currStageUsersQuery', 'currStageIssueTypes'],
  '/projects': ['project', 'orgProjectStages', 'orgProjects'],
  '/project_participants': ['getProjectStageParticipations', 'currStageUsersQuery'],
  '/project_participations': ['getProjectStageParticipations', 'userProjectParticipations'],
  '/project_roles': ['currStageIssueTypes'],
  '/organization_members': ['getOrgMembers'],
  '/organizations': ['currentOrganization'],
  '/users': ['user', 'getOrgMembers'],
  '/custom_issue_filters': ['currStageIssueTypes'],
}

/** Mutation URLs that should NOT trigger auto-invalidation (async operations, exports, etc.) */
const MUTATION_INVALIDATION_SKIP: string[] = [
  '/export-pdf',
  '/login',
  '/reset_password',
]

class NativeQueuedResponse {
  constructor(
    public readonly response: {
      data: { status: string; metadata: unknown }
      status: number
      statusText: string
      headers: Record<string, string>
      config: InternalAxiosRequestConfig
    },
    public readonly config: InternalAxiosRequestConfig,
  ) {}
}

/**
 * Check if an AxiosError is caused by being offline / no network.
 * These errors have no response (request never reached server) and should
 * not show a toast – the user already knows they're offline.
 */
const isOfflineOrTimeoutError = (error: AxiosError): boolean =>
  !navigator.onLine ||
  error.code === 'ERR_NETWORK' ||
  error.code === 'ECONNABORTED' ||
  error.message === 'Network Error'

const SEVEN_DAYS = 1000 * 60 * 60 * 24 * 7

const api = axios.create({
  baseURL: getApiBaseUrl() || '/',
  withCredentials: true,
  timeout: 15_000,
})

api.defaults.headers.common['Forward-To-Api'] = 'true'

api.defaults.headers.get['Accept'] = 'application/ld+json'

api.defaults.headers.patch['Accept'] = 'application/ld+json'
api.defaults.headers.patch['Content-Type'] = 'application/merge-patch+json'

api.defaults.headers.post['Accept'] = 'application/ld+json'
api.defaults.headers.post['Content-Type'] = 'application/ld+json'

const QUEUEABLE_ENDPOINTS = ['/issues', '/comments', '/media_objects', '/locations']

const checkNetworkConnected = async (): Promise<boolean> => {
  try {
    const status = await Network.getStatus()
    return status.connected
  } catch {
    return navigator.onLine
  }
}

// Always queue POST/PATCH requests first, then immediately sync if online
const handleNativeQueueRequest = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const method = config.method?.toUpperCase()
  const url = config.url || ''
  const isQueueable = QUEUEABLE_ENDPOINTS.some(endpoint => url.includes(endpoint))

  if ((method === 'POST' || method === 'PATCH') && isQueueable) {
    debug('iOS native queuing request:', url)

    let formData: FormData | undefined
    if (config.data instanceof FormData) {
      formData = config.data
    }

    const result = await nativeQueueManager.enqueueRequest(config, formData)

    // Immediately trigger sync if online (non-blocking)
    checkNetworkConnected()
      .then(isConnected => {
        if (isConnected) {
          debug('Online, triggering immediate sync')
          nativeQueueManager.performSync().catch(err => {
            debug('Sync failed, will retry later:', err)
          })
        }
      })
      .catch(err => {
        debug('Network check failed:', err)
      })

    const mockResponse = {
      data: { status: result.status, metadata: result.metadata },
      status: 202,
      statusText: 'Accepted',
      headers: {},
      config,
    }

    return Promise.reject(new NativeQueuedResponse(mockResponse, config))
  }

  return config
}

// Track request start times for slow connection detection (WeakMap avoids config mutation)
const requestStartTimes = new WeakMap<InternalAxiosRequestConfig, number>()

api.interceptors.request.use(async config => {
  requestStartTimes.set(config, Date.now())

  if (typeof window !== 'undefined') {
    config.headers['User-Locale'] = localStorage.getItem(LOCAL_STORAGE_LANG_KEY) || 'pl'

    const method = config.method?.toUpperCase()
    const url = config.url || ''
    const isQueueable = QUEUEABLE_ENDPOINTS.some(endpoint => url.includes(endpoint))
    const useQueue = shouldQueueMutation()

    debug(`Request: ${method} ${url}, isQueueable=${isQueueable}, useQueue=${useQueue}`)

    if (useQueue && (method === 'POST' || method === 'PATCH') && isQueueable) {
      debug('>>> Routing to native queue')
      return handleNativeQueueRequest(config)
    }
  }
  return config
})

api.interceptors.response.use(
  response => {
    const startTime = requestStartTimes.get(response.config)
    if (startTime !== undefined) {
      slowConnectionDetector.recordLatency(Date.now() - startTime)
      requestStartTimes.delete(response.config)
    }

    // Auto-invalidate React Query caches after successful mutations.
    // Queueable endpoints (/issues, /comments, /media_objects) are updated via
    // Mercure SSE after the Dexie queue syncs — no cache invalidation here.
    // All other endpoints: invalidate normally on successful mutation.
    const method = response.config.method?.toUpperCase()
    if (method && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
      const url = response.config.url ?? ''
      // Only suppress invalidation for methods actually routed through the
      // Dexie queue (POST/PATCH). DELETE goes straight to the server and
      // needs normal cache invalidation — Mercure alone is not sufficient
      // because the delete response arrives before the SSE event.
      const isQueuedMethod = method === 'POST' || method === 'PATCH'
      const isQueuedEndpoint = isQueuedMethod && QUEUEABLE_ENDPOINTS.some(endpoint => url.includes(endpoint))
      const shouldSkip = MUTATION_INVALIDATION_SKIP.some(skip => url.includes(skip))

      if (!isQueuedEndpoint && !shouldSkip) {
        for (const [segment, queryKeys] of Object.entries(MUTATION_INVALIDATION_MAP)) {
          if (url.includes(segment)) {
            for (const key of queryKeys) {
              queryClient.invalidateQueries({ queryKey: [key] })
            }
          }
        }
      }
    }

    return response
  },
  async error => {
    // Record latency for errors that reflect actual network round-trips:
    // - Responses (4xx/5xx): server was reached, latency is real
    // - Timeouts (ECONNABORTED): request hung, latency = timeout value
    // Skip ERR_NETWORK (truly offline) — not a latency signal.
    if (error instanceof AxiosError && error.config) {
      const startTime = requestStartTimes.get(error.config)
      if (startTime !== undefined) {
        if (error.response || error.code === 'ECONNABORTED') {
          slowConnectionDetector.recordLatency(Date.now() - startTime)
        }
        requestStartTimes.delete(error.config)
      }
    }

    if (error instanceof NativeQueuedResponse) {
      debug('Native queue request accepted')
      return error.response
    }

    if (error.response && error.request?.responseURL) {
      const relativePath = window.location.pathname + window.location.search + window.location.hash
      const loginPath = '/login'
      if (window.location.pathname === loginPath || error.request.responseURL.includes('/login')) {
        return Promise.reject(error)
      }

      if (error.response.status === 401) {
        console.error(`redirecting to ${loginPath} for ${relativePath} due to 401`)
        window.location.href = loginPath
        return Promise.reject(error)
      }

      // 403 on the auth endpoint means session expired (not a permission error on a resource)
      if (error.response.status === 403) {
        const requestUrl = error.config?.url || ''
        if (requestUrl.includes('/users/me')) {
          console.error(`redirecting to ${loginPath} for ${relativePath} due to 403 on auth endpoint`)
          window.location.href = loginPath
          return Promise.reject(error)
        }
      }
    }
    return Promise.reject(error)
  },
)

export { api }

// React Query
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: Error, query) => {
      debug('Query error:', error)
      if ((query.options as { throwOnError?: boolean }).throwOnError === false) return
      if (error instanceof AxiosError && !isOfflineOrTimeoutError(error)) {
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: error?.response?.data?.detail,
        })
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: Error) => {
      debug('Mutation error:', error)
      if (error instanceof AxiosError && !isOfflineOrTimeoutError(error)) {
        // TODO(server-409): Server currently uses last-write-wins and never returns 409.
        // This handler is forward-looking plumbing — it will activate when the server
        // adds updatedAt/ETag conflict checking on PATCH endpoints.
        // NOTE: MutationCache.onError fires BEFORE per-mutation onError callbacks.
        // The `return` below prevents the generic toast but does NOT prevent
        // mutation-level error handlers from also firing. If individual mutation hooks
        // add their own error handling, they should also guard against 409.
        if (error.response?.status === 409) {
          toast({
            variant: 'destructive',
            title: i18n.t('conflict.title'),
            description: i18n.t('conflict.description'),
          })
          const requestUrl = error.config?.url || ''
          if (requestUrl.includes('/issues')) {
            queryClient.invalidateQueries({ queryKey: ['singleIssue'] })
            queryClient.invalidateQueries({ queryKey: ['stageIssuesByIssueType'] })
          }
          if (requestUrl.includes('/comments')) {
            queryClient.invalidateQueries({ queryKey: ['singleIssueComments'] })
          }
          return
        }
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: error?.response?.data?.detail,
        })
      }
    },
  }),
  defaultOptions: {
    mutations: {
      networkMode: 'always',
    },
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 3,
      // gcTime must be >= maxAge in persistOptions for offline support
      // This keeps cached data available for 7 days for offline access
      gcTime: SEVEN_DAYS,
      refetchOnReconnect: 'always',
      // Don't throw errors to error boundaries - handle via isError state instead
      // This prevents offline/network errors from crashing the app
      throwOnError: false,
      retry: false,
    },
  },
})
